export type Timeframe = 'dollar' | 'volume' | '1m' | '5m' | '15m' | '30m' | '1H' | '2H' | '4H' | '8H' | '1D' | '1W' | '1M';

export const TIMEFRAMES: readonly Timeframe[] = ['dollar', 'volume', '1m', '5m', '15m', '30m', '1H', '2H', '4H', '8H', '1D', '1W', '1M'];

/** Information bar types that represent non-time-indexed bar constructions */
export const INFO_BAR_TIMEFRAMES: ReadonlySet<Timeframe> = new Set<Timeframe>(['dollar', 'volume']);

/** Timeframes with actual data in ClickHouse (others show 'No data') */
export const AVAILABLE_TIMEFRAMES: ReadonlySet<Timeframe> = new Set<Timeframe>(['dollar', 'volume', '1m', '5m', '15m', '30m', '1H', '2H', '4H', '8H', '1D']);

export type ConnStatus = 'Connected' | 'Disconnected' | 'NotConfigured';

/** Live per-upstream health (DDD #180 S1.2 · additive · gateway sends when available). */
export interface UpstreamHealth {
  redis: boolean;
  clickhouse: boolean;
  bybit: boolean;
  regime: boolean;
}

export interface HealthResponse {
  status: string;
  version: string;
  uptime_secs: number;
  datasource: string;
  clickhouse: ConnStatus;
  redis: ConnStatus;
  /** Additive (DDD #180 S1.2). Absent on older gateways → treat as unknown (all healthy). */
  upstream_health?: UpstreamHealth;
}

/** Per-panel data lifecycle (DDD #180 S1.2). */
export type PanelDataState = 'loading' | 'ready' | 'live-empty' | 'degraded';

/** Upstream source a panel depends on; undefined = no single upstream (use client fetch outcome only). */
export type UpstreamSource = keyof UpstreamHealth;

export interface RdLoopState {
  active_hypothesis: string | null;
  current_step: string | null;
  last_result: string | null;
  updated_at: string | null;
}

export interface JobStatus {
  name: string;
  status: string;
  created: string | null;
  duration: string | null;
}

export type BacktestingOperatorPreset = 'bounded_h10_summary_v1';
export type BacktestingOperatorRunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'timed_out';
export type BacktestingOperatorAuditMilestone = 'accepted' | 'started' | 'terminal' | 'cleanup';

export interface BacktestingOperatorRunStartResponse {
  run_id: string;
  preset: BacktestingOperatorPreset;
  status: BacktestingOperatorRunStatus;
  runner_profile: 'tm_backtest_operator_bounded_v1';
  max_concurrency: 1;
  timeout_seconds: 900;
  cpu_limit: '1';
  memory_limit_mib: 2048;
  max_output_bytes: 65536;
  idempotent_replay: boolean;
  runtime_execution_executed: boolean;
  claim_promotion_allowed: false;
}

export interface BacktestingOperatorRunStatusResponse {
  run_id: string;
  preset: BacktestingOperatorPreset;
  status: BacktestingOperatorRunStatus;
  runner_profile: 'tm_backtest_operator_bounded_v1';
  created_at_utc: string;
  updated_at_utc: string;
  timeout_seconds: 900;
  summary_available: boolean;
  failure_code: string | null;
  runtime_execution_executed: boolean;
  claim_status: 'UNVERIFIED_NOT_PROMOTED';
  lifecycle: BacktestingOperatorAuditMilestone[];
  cleanup_verified: boolean;
}

export interface BacktestingValidatedSummary {
  manifest_schema: 'tm_backtest_operator_summary_manifest_v1';
  manifest_version: 'tm-backtest.operator-summary.v1';
  strategy_id: string;
  selected_config_id: 'bounded_h10_summary_v1';
  instrument_count: number;
  timeframe_or_bar_spec: string;
  window_start_utc: string;
  window_end_utc: string;
  artifact_kind: 'backtest_run_manifest';
  artifact_digest_present: boolean;
  tm_eval_report_ref_present: boolean;
  score_summary: Record<string, number>;
  risk_summary: Record<string, number>;
  non_claims: string[];
}

export interface BacktestingOperatorRunResultResponse {
  run_id: string;
  preset: BacktestingOperatorPreset;
  status: 'succeeded';
  claim_status: 'UNVERIFIED_NOT_PROMOTED';
  summary: BacktestingValidatedSummary;
  raw_artifact_returned: false;
  raw_rows_returned: false;
  claim_promotion_allowed: false;
}

// Mirrors Rust WsMessage enum from tm-gateway-types (DDD #116 S1-S6)
export type WsMessage =
  | { type: 'RdLoopUpdate'; data: RdLoopState }
  | { type: 'JobUpdate'; data: JobStatus[] }
  | { type: 'LogLine'; data: string }
  | { type: 'PositionUpdate'; data: GatewayPosition[] }
  | { type: 'MetricsUpdate'; data: PortfolioMetrics }
  | { type: 'ArsenalUpdate'; data: ArsenalCombo[] }
  | { type: 'Heartbeat' }
  | { type: 'BarClose'; data: BarData }
  | { type: 'ComboStatus'; data: ComboStatus }
  | { type: 'KillSwitchAck'; data: { armed: boolean } }
  | { type: 'ComboWeightAck'; data: { combo: string; weight: number } }
  | { type: 'StrategyState'; data: StrategyState }
  | { type: 'ComboGenome'; data: ComboGenome }
  | { type: 'AlphaEval'; data: AlphaEvalReport }
  | { type: 'RegimeUpdate'; data: RegimeState }
  | { type: 'OrderUpdate'; data: OrderData }
  | { type: 'OrderbookUpdate'; data: OrderbookData }
  | { type: 'KillSwitchEvent'; data: { action: 'arm' | 'kill' | 'resume'; timestamp: string } };

export interface OrderRequest {
  symbol: string;
  side: 'BUY' | 'SELL';
  order_type: 'MARKET' | 'LIMIT' | 'STOP';
  qty: number;
  price: number | null;
  stop_price: number | null;
}

export interface OrderResponse {
  order_id: string;
  status: string;
  rejection_reason?: string;
  demo_account?: boolean;
  block_ttl_seconds?: number;
  operator_override_state?: string;
}

export interface ExchangeReferenceDriftTradingBlockStatus {
  status: string;
  demo_account: boolean;
  demo_credential_configured: boolean;
  venue: string;
  instrument: string;
  provider_symbol: string;
  timeframe: string;
  enforcement_mode: string;
  block_status: string;
  block_reason: string;
  threshold_bps: number;
  block_ttl_seconds: number;
  block_expires_at_utc: string | null;
  operator_override_state: string;
  operator_override_audit_id: string | null;
  order_path_block_active: boolean;
  trading_block_recommended: boolean;
  shadow_would_block: boolean;
  trading_block_enforcement_allowed: boolean;
  trading_block_enforced: boolean;
  real_capital_allowed: boolean;
  mainnet_order_mutation_allowed: boolean;
  withdrawal_transfer_allowed: boolean;
  scope: {
    window_start_utc: string;
    window_end_utc: string;
  };
  drift_status: string;
  max_price_bps_diff: number;
  mutation_flags: {
    demo_order_block_rejection_allowed: boolean;
    demo_order_submission_allowed_while_blocked: boolean;
    real_capital_allowed: boolean;
    mainnet_order_mutation_allowed: boolean;
    withdrawal_transfer_allowed: boolean;
  };
}

// Gateway-specific types (from tm-gateway-types crate)
export interface GatewayPosition {
  symbol: string;
  side: string;
  quantity: number;
  entry_price: number;
  current_price: number;
  unrealized_pnl: number;
  realized_pnl: number;
  updated_at: string;
}

export interface PortfolioMetrics {
  total_equity: number;
  daily_pnl: number;
  daily_pnl_pct: number;
  sharpe_30d: number;
  max_drawdown_pct: number;
  win_rate: number;
  open_positions: number;
  updated_at: string;
}

export interface ArsenalCombo {
  combo_id: string;
  name: string;
  thesis: string;
  status: string;
  sharpe: number;
  max_dd_pct: number;
  win_rate: number;
  total_trades: number;
  last_signal: string | null;
}

export type RdLoopCommandType = 'EXPLORE' | 'DESIGN' | 'TRAIN' | 'SIMULATE' | 'EVALUATE' | 'SCORE' | 'REGIME';

export interface DispatchRequest {
  command: {
    type: RdLoopCommandType;
    payload: { hypothesis_id?: string; config_path?: string; model_id?: string };
  };
}

export interface DispatchResponse {
  status: string;
  message: string;
  job_id: string | null;
}

export interface GatewayBarData {
  symbol: string;
  bar_type: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  bar_close_time: string;
}

export interface BarData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  duration_s?: number;
}

export interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface HMMState {
  name: string;
  label: string;
  probability: number;
  color: string;
}

export interface RiskMetrics {
  evt_es_99: number;
  var_95: number;
  liquidation_cascade_prob: number;
  garch_vol: number;
  implied_skew: string;
}

export interface AssetAllocation {
  name: string;
  weight: string;
  weight_label: string;
  z_score: number;
  factor: string;
  action: string;
  action_color: string;
  border_hover_color: string;
}

export interface PortfolioData {
  target_sharpe: number;
  assets: AssetAllocation[];
}

export interface ComboStatus {
  combo: string;
  win_rate: number;
  pnl: number;
  weight: number;
  cusum_score: number;
  enabled: boolean;
}

export interface SignalData {
  id: string;
  time: number;
  side: 'BUY' | 'SELL';
  qty: number;
  price: number;
  pnl: number;
}

export interface OrderData {
  id: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  type: string;
  qty: number;
  price: number;
  status: 'OPEN' | 'FILLED' | 'CANCELLED' | 'REJECTED';
  tags?: string;
}

export interface PositionData {
  symbol: string;
  qty: number;
  entry_price: number;
  current_price: number;
  unrealized_pnl: number;
  view: 'HEDGING' | 'NETTING';
  strategy?: string;
}

export type ComponentState =
  | 'PRE_INITIALIZED' | 'READY' | 'STARTING' | 'RUNNING'
  | 'STOPPING' | 'STOPPED' | 'RESUMING' | 'RESETTING'
  | 'DISPOSING' | 'DISPOSED' | 'DEGRADING' | 'DEGRADED'
  | 'FAULTING' | 'FAULTED';

export interface ComboGenome {
  name: string;
  bar_type: string;
  instrument: string;
  model_path: string;
  strategy_class: string;
  tactic: string;
  features: {
    extended: boolean;
    warmup_bars: number;
    feature_count: number;
    engine: string;
  };
  bracket: {
    direction_mode: string;
    tp_pct: number;
    sl_pct: number;
    snr_threshold: number;
    k: number;
  };
  scoring: {
    tier: string;
    score: number;
    last_scored: number;
  };
  model_meta: {
    model_type: string;
    trained_date: string;
    ic_at_training: number;
    inference_ms: number;
  };
}

export interface FloorGate {
  value: number | boolean;
  pass: boolean;
}

export interface AlphaEvalReport {
  combo: string;
  ts: number;
  floor_gates: Record<string, FloorGate>;
  core_metrics: Record<string, number>;
  advanced_metrics: Record<string, number>;
  per_regime: Record<string, {
    sharpe: number;
    wr: number;
    ic: number;
    trades: number;
  }>;
}

export interface AccountBalance {
  wallet_balance: number;
  margin_balance: number;
  available_balance: number;
  unrealized_pnl: number;
  updated_at: string;
}

export interface ExternalFeed {
  source: string;
  instrument: string | null;
  value: unknown;
  updated_at: string;
}

export interface RegimeState {
  state: string;
  probabilities: Record<string, number>;
  timestamp: string;
}

export interface StrategyState {
  strategy_id: string;
  state: ComponentState;
  previous_state: ComponentState;
  ts: number;
}

export interface InstrumentInfo {
  symbol: string;
  last_price: number;
  change_24h: number;
  volume_24h: number;
}

export interface HyperliquidAddressSync {
  venue: string;
  status: string;
  source: string;
  endpoint_family: string;
  address_mask: string;
  lookback_days: number;
  fill_count: number;
  unique_instruments: string[];
  first_fill_time_ms: number | null;
  last_fill_time_ms: number | null;
  fetched_at: string;
  read_only: boolean;
  messages: string[];
}

export type ReadOnlyExchangeVenue = 'Bybit' | 'Binance' | 'OKX' | 'HyperLiquid';
export type ReadOnlyExchangeSyncStatus = 'synced' | 'empty';

export interface ReadOnlyExchangeSyncSummary {
  venue: ReadOnlyExchangeVenue;
  status: ReadOnlyExchangeSyncStatus;
  source_endpoints: string[];
  endpoint_family: string;
  lookback_days: number;
  window_start_ms: number;
  window_end_ms: number;
  closed_pnl_row_count: number;
  transaction_log_row_count: number;
  unique_instruments: string[];
  fetched_at: string;
  read_only: boolean;
  messages: string[];
}

export type ReadOnlyConnectorStatus = 'contract_only';

export interface ReadOnlyConnectorCapabilityPolicy {
  contract_version: string;
  connector_kind: 'server_exchange_api' | 'wallet_address_summary' | string;
  credential_scope: string;
  credential_storage_policy: string;
  browser_secret_storage_allowed: boolean;
  server_side_secret_required: boolean;
  read_only_request_auth_required: boolean;
  wallet_signature_allowed: boolean;
  order_signature_allowed: boolean;
  order_endpoints_allowed: boolean;
  withdrawal_endpoints_allowed: boolean;
  transfer_endpoints_allowed: boolean;
  wallet_export_import_allowed: boolean;
  wallet_delegation_allowed: boolean;
  raw_payload_returned: boolean;
  summary_only_output: boolean;
}

export interface ReadOnlyConnectorContract {
  venue: ReadOnlyExchangeVenue;
  status: ReadOnlyConnectorStatus;
  endpoint_family: string;
  source_endpoints: string[];
  request_methods: string[];
  credential_surface: string;
  capability_policy: ReadOnlyConnectorCapabilityPolicy;
  summary_only: boolean;
  raw_rows_returned: boolean;
  mutation_endpoints_allowed: boolean;
  live_sync_enabled: boolean;
  messages: string[];
  blocked_actions: string[];
}

export interface ReadOnlyConnectorCatalog {
  status: string;
  read_only: boolean;
  contracts: ReadOnlyConnectorContract[];
  messages: string[];
}

export type ReadOnlyIngestionStatus =
  | 'synced'
  | 'empty'
  | 'partial'
  | 'not_configured'
  | 'contract_only'
  | 'upstream_error';

export interface ReadOnlyIngestionVenueSummary {
  venue: ReadOnlyExchangeVenue;
  status: ReadOnlyIngestionStatus;
  source: string;
  endpoint_family: string;
  source_endpoints: string[];
  request_methods: string[];
  credential_surface: string;
  capability_policy: ReadOnlyConnectorCapabilityPolicy;
  lookback_days: number | null;
  window_start_ms: number | null;
  window_end_ms: number | null;
  row_count: number;
  unique_instruments: string[];
  first_event_time_ms: number | null;
  last_event_time_ms: number | null;
  fetched_at: string | null;
  read_only: boolean;
  summary_only: boolean;
  raw_rows_returned: boolean;
  mutation_endpoints_allowed: boolean;
  live_sync_enabled: boolean;
  messages: string[];
  blocked_actions: string[];
}

export interface ReadOnlyIngestionResponse {
  status: ReadOnlyIngestionStatus;
  read_only: boolean;
  summary_only: boolean;
  requested_venues: ReadOnlyExchangeVenue[];
  results: ReadOnlyIngestionVenueSummary[];
  messages: string[];
}

export interface TradingDnaReportSource {
  venue: string;
  file_name: string;
  row_count: number;
  truncated: boolean;
}

export interface TradingDnaReportInputRow {
  source_row: number;
  venue: string;
  instrument: string;
  side: string;
  quantity: string;
  price: string;
  pnl: string;
  fee: string;
  funding: string;
  timestamp: string;
  leverage: string;
}

export interface TradingDnaReportRequest {
  source: TradingDnaReportSource;
  rows: TradingDnaReportInputRow[];
}

export interface TradingDnaReportSummary {
  venue: string;
  row_count: number;
  pnl_count: number;
  fee_count: number;
  funding_count: number;
  unique_instruments: string[];
  total_net_pnl: number;
  total_fees: number;
  total_funding: number;
  gross_pnl_before_costs: number;
}

export interface TradingDnaTradePoint {
  id: string;
  source_row: number;
  venue?: string;
  instrument: string;
  side: string;
  timestamp: string;
  timestamp_ms: number | null;
  pnl: number;
  fee: number;
  funding: number;
  leverage: number | null;
  cumulative_pnl: number;
  marker_position_pct: number;
  tone: 'positive' | 'negative' | 'neutral' | string;
}

export interface TradingDnaBucketSummary {
  label: string;
  count: number;
  net_pnl: number;
}

export interface TradingDnaStreakSummary {
  longest_win_streak: number;
  longest_loss_streak: number;
  current_streak: string;
}

export interface TradingDnaCostImpactSummary {
  gross_pnl_before_costs: number;
  total_fees: number;
  total_funding: number;
  net_pnl_after_costs: number;
}

export interface TradingDnaVisualizationSummary {
  trade_points: TradingDnaTradePoint[];
  outcome_buckets: TradingDnaBucketSummary[];
  holding_time_buckets: TradingDnaBucketSummary[];
  leverage_buckets: TradingDnaBucketSummary[];
  streaks: TradingDnaStreakSummary;
  cost_impact: TradingDnaCostImpactSummary;
}

export interface TradingDnaProviderSignal {
  status: string;
  provider: string;
  provider_id?: string;
  source: string;
  source_kind?: string;
  freshness: string;
  freshness_timestamp_utc?: string | null;
  fetched_at_utc?: string | null;
  stale_after_utc?: string | null;
  cache_key?: string;
  cache_policy?: string;
  provider_backed?: boolean;
  cost_rate_limit_class?: string;
  unavailable_reason?: string;
  citation_label?: string;
  evidence_state?: string;
  summary: string;
  provenance: string;
  caveats: string[];
  capability?: TradingDnaProviderCapability;
}

export interface TradingDnaProviderCapability {
  slot: string;
  data_kind: string;
  source_policy: string;
  provider_backed: boolean;
  live_fetch_allowed: boolean;
  cache_hydration_allowed: boolean;
  claim_boundary: string;
}

export interface TradingDnaProviderAdapterStatus {
  slot: string;
  adapter_id: string;
  status: string;
  provider: string;
  provider_id?: string;
  source_kind?: string;
  authority: string;
  cache_status: string;
  cache_key: string;
  cache_policy?: string;
  freshness: string;
  freshness_timestamp_utc?: string | null;
  fetched_at_utc?: string | null;
  stale_after_utc?: string | null;
  provider_backed?: boolean;
  live_fetch_allowed?: boolean;
  cache_hydration_allowed?: boolean;
  cost_rate_limit_class?: string;
  unavailable_reason?: string;
  citation_label?: string;
  evidence_state?: string;
  summary: string;
  provenance: string;
  required_inputs: string[];
  blocked_inputs: string[];
  caveats: string[];
  capability?: TradingDnaProviderCapability;
}

export interface TradingDnaProviderProofScope {
  provider: string;
  provider_id: string;
  slot: string;
  instrument_scope: string;
  window_start_utc?: string | null;
  window_end_utc?: string | null;
}

export interface TradingDnaProviderCacheProof {
  evidence_state: string;
  proof_mode: string;
  scope: TradingDnaProviderProofScope;
  cache_key: string;
  source_citation: string;
  cost_rate_limit_class: string;
  fetched_at_utc?: string | null;
  stale_after_utc?: string | null;
  provider_backed: boolean;
  claim_promotion_allowed: boolean;
  raw_payload_available: boolean;
  secret_surface: string;
  validation_errors: string[];
  non_claims: string[];
}

export interface TradingDnaProviderProofLedger {
  version: string;
  status: string;
  read_only: boolean;
  claim_promotion_allowed: boolean;
  proof_scope_required: string;
  proofs: TradingDnaProviderCacheProof[];
  blocked_inputs: string[];
  promotion_requirements: string[];
  non_claims: string[];
  caveats: string[];
}

export interface TradingDnaProviderContext {
  status: string;
  generated_by: string;
  read_only: boolean;
  fundamentals: TradingDnaProviderSignal;
  liquidity_depth: TradingDnaProviderSignal;
  funding: TradingDnaProviderSignal;
  price_reference: TradingDnaProviderSignal;
  registry?: TradingDnaProviderAdapterStatus[];
  proof_ledger?: TradingDnaProviderProofLedger;
  caveats: string[];
}

export interface TradingDnaInstrumentReport {
  venue?: string;
  instrument: string;
  summary: string;
  fundamental_status: string;
  provider_context?: TradingDnaProviderContext;
  trade_count: number;
  net_pnl: number;
  total_fees: number;
  total_funding: number;
  win_count: number;
  loss_count: number;
  avg_pnl: number;
  liquidity_note: string;
  funding_note: string;
  provenance: string;
  caveats: string[];
}

export interface TradingDnaAthenaProviderContextPacket {
  version: string;
  read_only: boolean;
  provider_contract_version?: string;
  provider_slots: string[];
  adapter_ids: string[];
  allowed_inputs: string[];
  blocked_inputs: string[];
  evidence_states?: string[];
  unavailable_reasons?: string[];
  citation_labels?: string[];
  capability_matrix?: TradingDnaProviderCapability[];
  provider_backed_claims?: string[];
  proof_ledger?: TradingDnaProviderProofLedger;
  cache_policy: string;
  freshness_policy?: string;
  cost_rate_limit_policy?: string;
  provenance_policy: string;
  non_claims: string[];
}

export interface TradingDnaAthenaReadOnlyIngestionVenue {
  venue: string;
  status: string;
  source: string;
  endpoint_family: string;
  credential_surface: string;
  capability_policy: ReadOnlyConnectorCapabilityPolicy;
  row_count: number;
  unique_instrument_count: number;
  read_only: boolean;
  summary_only: boolean;
  raw_rows_returned: boolean;
  mutation_endpoints_allowed: boolean;
  live_sync_enabled: boolean;
}

export interface TradingDnaAthenaReadOnlyIngestionPacket {
  version: string;
  read_only: boolean;
  summary_only: boolean;
  status: string;
  requested_venues: string[];
  venues: TradingDnaAthenaReadOnlyIngestionVenue[];
  allowed_inputs: string[];
  blocked_inputs: string[];
  provenance_policy: string;
  non_claims: string[];
}

export interface TradingDnaInternalBarProofClaim {
  version: string;
  status: string;
  source: string;
  instrument: string;
  provider_symbol: string;
  timeframe: string;
  window_start_utc: string;
  window_end_utc: string;
  comparison: string;
  nt_bar_count: number;
  clickhouse_execution_ordered_bar_count: number;
  matched_minutes: number;
  mismatch_minutes: number;
  max_abs_price_diff: string;
  max_abs_volume_diff: string;
  evidence_ref: string;
  verified_scope: string;
  provider_backed_claims_verified: boolean;
  athena_authority_verified: boolean;
  non_claims: string[];
}

export interface TradingDnaAthenaContext {
  status: string;
  scope: string;
  summary: string;
  instruments: string[];
  report_sections: string[];
  provider_context_packet?: TradingDnaAthenaProviderContextPacket;
  read_only_ingestion_packet?: TradingDnaAthenaReadOnlyIngestionPacket;
  internal_bar_proof?: TradingDnaInternalBarProofClaim;
  allowed_topics: string[];
  blocked_topics: string[];
  caveats: string[];
}

export interface TradingDnaTmEvalSummary {
  n_cycles: number;
  mean_hold_bars: number;
  mean_pnl: number;
  deployment_tier: string;
  floor_all_pass: boolean;
  f1_dsr: number;
  f3_mdd: number;
  c1_sharpe: number;
  c6_cvar_95: number;
  c7_profit_factor: number;
  a9_max_consec_loss: number;
  bars_per_year: number;
  pnl_scale: number;
}

export interface TradingDnaReportStageResult {
  id: string;
  label: string;
  status: string;
  output: string;
}

export interface TradingDnaReportResponse {
  status: string;
  source: TradingDnaReportSource;
  eval_authority: string;
  read_only: boolean;
  fetched_at: string;
  summary: TradingDnaReportSummary;
  tm_eval: TradingDnaTmEvalSummary;
  visualizations: TradingDnaVisualizationSummary;
  provider_context?: TradingDnaProviderContext;
  instrument_reports: TradingDnaInstrumentReport[];
  athena_context: TradingDnaAthenaContext;
  stages: TradingDnaReportStageResult[];
  messages: string[];
}

export interface CopilotEntry {
  tag: string;
  tag_color: string;
  text: string;
  anchor_id?: string;
}

export interface OrderbookLevel {
  price: number;
  size: number;
}

export interface OrderbookData {
  symbol: string;
  bids: OrderbookLevel[];
  asks: OrderbookLevel[];
  /** Exchange event timestamp in milliseconds when provided by the gateway. */
  timestamp?: number;
}

export interface VarConeResponse {
  symbol: string;
  dates: string[];
  p05: number[];
  p25: number[];
  p50: number[];
  p75: number[];
  p95: number[];
}

/**
 * Single HMM regime observation from GET /api/regime/history
 * state: 0=Bull-Low · 1=Bull-High · 2=Bear-Low · 3=Bear-High
 */
export interface RegimeRecord {
  timestamp_ms: number;
  state: 0 | 1 | 2 | 3;
  p_high_vol: number;
  state_name: string; // "Bull-Low" | "Bull-High" | "Bear-Low" | "Bear-High"
}

/**
 * Single portfolio equity observation from GET /api/portfolio/equity
 * timestamp_ms: Unix epoch in milliseconds
 * equity: total portfolio equity in USD
 * pnl_realized: cumulative realized PnL in USD
 * pnl_unrealized: current unrealized PnL in USD
 * drawdown_pct: fraction from peak equity (0–1 range)
 */
export interface EquityRecord {
  timestamp_ms: number;
  equity: number;
  pnl_realized: number;
  pnl_unrealized: number;
  drawdown_pct: number;
}

// DDD #189 S0-S1-S3: wallet-native readonly boundary.
export type WalletSessionScope = 'readonly';
export type WalletSessionCapability = 'portfolio_read' | 'security_score_read';
export type WalletVerificationStatus =
  | 'challenge_issued'
  | 'challenge_signed_unverified_beta'
  | 'challenge_signed_verified';

export interface WalletSessionNonceRequest {
  address: string;
  chain_id: number;
}

export interface WalletSessionNonceResponse {
  address: string;
  chain_id: number;
  nonce: string;
  message: string;
  issued_at: string;
  expires_at: string;
  scope: WalletSessionScope;
  verification_status: WalletVerificationStatus;
}

export interface WalletSessionRequest {
  address: string;
  chain_id: number;
  nonce: string;
  message: string;
  signature: string;
}

export interface WalletSessionResponse {
  session_id: string;
  address: string;
  chain_id: number;
  scope: WalletSessionScope;
  capabilities: WalletSessionCapability[];
  verification_status: WalletVerificationStatus;
  verified: boolean;
  issued_at: string;
  expires_at: string;
}

export interface WalletSessionRevokeResponse {
  session_id: string;
  revoked: boolean;
}

export interface WalletSecurityReportRequest {
  address: string;
  chain_id: number;
  fixture_mode?: boolean;
}

export type WalletRiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface ApprovalExposure {
  chain_id: number;
  token_symbol: string;
  token_address: string;
  spender_address: string;
  spender_label: string;
  allowance: string;
  allowance_usd: number;
  risk_level: WalletRiskLevel;
  last_updated: string;
}

export interface ContractRisk {
  contract_address: string;
  label: string;
  risk_level: WalletRiskLevel;
  reasons: string[];
}

export interface RecommendedAction {
  id: string;
  severity: WalletRiskLevel;
  action: string;
  target: string;
  rationale: string;
}

export interface WalletSecuritySummary {
  score: number;
  grade: string;
  risk_level: WalletRiskLevel;
  critical_approvals: number;
  high_approvals: number;
  total_allowance_usd: number;
}

export interface WalletSecurityReport {
  address: string;
  chain_id: number;
  generated_at: string;
  data_source: string;
  summary: WalletSecuritySummary;
  approvals: ApprovalExposure[];
  contract_risks: ContractRisk[];
  recommended_actions: RecommendedAction[];
}

// DDD #189 W3: embedded wallet beta control shell. Provider SDK remains blocked.
export type WalletSensitiveAction =
  | 'embedded_wallet_create'
  | 'export'
  | 'import'
  | 'sign'
  | 'delegate'
  | 'order';

export interface WalletControlStatusResponse {
  request_id: string;
  audit_id: string;
  evaluated_at: string;
  embedded_wallet_beta_enabled: boolean;
  kill_switch_armed: boolean;
  control_source: string;
  allowed_actions: WalletSensitiveAction[];
  denied_actions: WalletSensitiveAction[];
  reason: string;
  audit_recorded: boolean;
}

export interface WalletActionDecisionResponse {
  request_id: string;
  audit_id: string;
  evaluated_at: string;
  action: WalletSensitiveAction;
  allowed: boolean;
  reason: string;
  embedded_wallet_beta_enabled: boolean;
  kill_switch_armed: boolean;
  control_source: string;
  audit_recorded: boolean;
}

export interface WalletEmbeddedCreateRequest {
  provider: string;
  chain_id: number;
  address?: string;
  session_id?: string;
}

export type WalletEmbeddedProviderStatus =
  | 'provider_auth_requested'
  | 'provider_create_requested'
  | 'provider_wallet_available';

export interface WalletEmbeddedStatusRequest {
  provider: string;
  chain_id: number;
  status: WalletEmbeddedProviderStatus;
  wallet_count: number;
  account_count: number;
  create_request_id: string;
}

export interface WalletEmbeddedStatusResponse {
  request_id: string;
  audit_id: string;
  evaluated_at: string;
  provider: string;
  chain_id: number;
  status: WalletEmbeddedProviderStatus;
  wallet_count: number;
  account_count: number;
  allowed: boolean;
  reason: string;
  embedded_wallet_beta_enabled: boolean;
  kill_switch_armed: boolean;
  control_source: string;
  audit_recorded: boolean;
}

/**
 * Institutional-dark color tokens for each regime state.
 * 0=Bull-Low (green) · 1=Bull-High (yellow) · 2=Bear-Low (blue) · 3=Bear-High (red)
 */
export const REGIME_STATE_COLORS: Record<0 | 1 | 2 | 3, string> = {
  0: '#01E599', // Bull-Low  — muted green
  1: '#F59E0B', // Bull-High — amber/yellow
  2: '#00E5FF', // Bear-Low  — cyan/blue
  3: '#FF3366', // Bear-High — muted red
} as const;
